import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { useSnackbar } from '@experiences/util';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApDropdown,
    ApDropdownItem,
    ApProgressSpinner,
} from '@uipath/portal-shell-react';
import type { ApDropdownCustomEvent } from '@uipath/portal-shell-types';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import type { Connection } from '../../../../services/connector/Connector.types';
import {
    createConnection,
    getConnectorConnections,
} from '../../../../services/connector/ConnectorService';

interface ConnectionDropdownProps {
    organizationId: string;
    tenantId: string;
    folderKey?: string;
    connectorKey?: string;
    label?: string;
    onChange: (value: Connection) => void;
    value?: string;
    required?: boolean;
    disabled?: boolean;
}

interface GroupedConnections {
    [folderKey: string]: Connection[];
}

export const ConnectionDropdown: React.FC<ConnectionDropdownProps> = ({
    organizationId, tenantId, folderKey, connectorKey, label, onChange, value, required, disabled,
}) => {
    const { formatMessage: translate } = useIntl();
    const [ data, setData ] = useState<Connection[]>();
    const [ error, setError ] = useState<string>();
    const [ fetching, setFetching ] = useState<boolean>();
    const [ creating, setCreating ] = useState<boolean>();
    const { enqueueSnackbar } = useSnackbar();

    const intervalRef = useRef<number>();

    const fetchData = useCallback(async () => {
        if (!connectorKey) {
            return [];
        }
        try {
            let connections = await getConnectorConnections(organizationId, tenantId, connectorKey);
            connections = connections.filter(c => !folderKey || c.folder.key === folderKey);
            setData(connections);
            return connections;
        } catch (e: any) {
            setError(e.message);
            return [];
        }
    }, [ connectorKey, folderKey, organizationId, tenantId ]);

    useEffect(() => {
        if (connectorKey) {
            setError(undefined);
            setCreating(false);
            setFetching(true);
            fetchData().finally(() => setFetching(false));
        }
    }, [ connectorKey, fetchData ]);

    const onAdd = useCallback(async () => {
        if (!connectorKey) {
            return;
        }

        const newTab = window.open('about:blank', '_blank');

        if (!newTab) {
            return;
        }

        // Prevent the new tab from accessing the opener window
        newTab.opener = null;

        setCreating(true);
        try {
            const connection = await createConnection(organizationId, tenantId, folderKey, connectorKey);
            if (connection.authUrl) {
                // Writes a minimal HTML document into the new tab that:
                // 1. Sets a "no-referrer" policy to prevent the target site from receiving the original page URL.
                // 2. Immediately redirects to the auth URL using a meta refresh.
                // This approach maintains a Window reference while preventing Referer leakage.
                newTab.document.write(`
                  <meta name="referrer" content="no-referrer">
                  <meta http-equiv="refresh" content="0;url=${connection.authUrl}">
                `);
                newTab.document.close();

                // Clear any existing interval
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = undefined;
                }

                const latestConnectionsData = await fetchData();
                const oldConnectionIds = new Set(latestConnectionsData.map(c => c.id));

                const interval = window.setInterval(() => {
                    fetchData()
                        .then((latestData) => {
                            const newConnectionIds = new Set(latestData.map(c => c.id));
                            const hasNewConnections = Array.from(newConnectionIds).some(id => !oldConnectionIds.has(id));

                            // Check if there are any new connections or if the tab was closed
                            if (hasNewConnections || newTab.closed) {
                                clearInterval(interval);
                                setCreating(false);
                            }
                        })
                        .catch(() => {
                            clearInterval(interval);
                            setCreating(false);
                        });
                }, 2000);
                intervalRef.current = interval;
            }
        } catch (e: any) {
            enqueueSnackbar(e.response?.data?.message ?? e.message, { variant: 'error' });
            setCreating(false);
        }
    }, [ connectorKey, enqueueSnackbar, fetchData, folderKey, organizationId, tenantId ]);

    const groupedConnections = useMemo(() => data?.reduce((acc: GroupedConnections, connection: Connection) => {
        const groupKey = connection.folder.path;
        if (!acc[groupKey]) {
            acc[groupKey] = [];
        }
        acc[groupKey].push(connection);
        return acc;
    }, {}), [ data ]);

    const onValueChanged = useCallback(async (event: ApDropdownCustomEvent<string | string[]>) => {
        if (event.detail === 'add-new-connection') {
            return onAdd();
        }

        const connection = data?.find(c => c.id === event.detail);
        if (connection) {
            onChange(connection);
        }
    }, [ data, onAdd, onChange ]);

    if (!connectorKey && !disabled) {
        return null;
    }

    if (creating || fetching) {
        return (
            <UiStack direction="column">
                <UiText
                    style={{
                        marginTop: 8,
                        marginBottom: 8,
                    }}
                    variant={FontVariantToken.fontSizeMBold}
                    color="var(--color-foreground-de-emp)">
                    {label}
                </UiText>
                <ApProgressSpinner />
            </UiStack>
        );
    }

    return (
        <UiStack
            direction="row"
            align="end"
            gap={SpacingToken.XS}>
            <ApDropdown
                required={required}
                disabled={disabled}
                style={{ width: '240px' }}
                label={label}
                selectedValue={value}
                data-cy="connections-dropdown"
                errorMessage={error}
                onSelectedValueChanged={onValueChanged}>
                <ApDropdownItem
                    key='add-new-connection'
                    value='add-new-connection'
                    label={'+ ' + translate({ id: 'CLIENT_ADD_CONNECTION' })} />
                {Object.entries(groupedConnections ?? {}).map(([ theFolderKey, connections ]) => [
                    ...connections.map((connection: Connection) => (
                        <ApDropdownItem
                            key={connection.id}
                            value={connection.id}
                            label={`${connection.name} - ${theFolderKey}`} />
                    )),
                ])}
            </ApDropdown>
        </UiStack>
    );
};
